import React, { FunctionComponent } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import PeopleIcon from '@material-ui/icons/People';
import ScheduleIcon from '@material-ui/icons/Schedule';
import colors from '../../../../../themes/main-colors';
import { useTranslation } from '../../../../../hooks/useTranslation';
import dayjs from 'dayjs';
import { formatEstimatedTimeOn } from '../../../outageUtils';
import { GuestOutageDetail } from '../OutageResultsPanel';
import Maybe from 'graphql/tsutils/Maybe';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: '10px -10px',
      display: 'flex',
      flexDirection: 'column',
    },
    infoRow: {
      display: 'flex',
      color: colors.noirBlur,
      margin: '5px',
    },
    infoIcon: {
      margin: '0 5px 0 10px',
      height: '24px',
      width: '24px',
    },
    infoContent: {
      marginLeft: '10px',
    },
    infoUpdate: {
      fontSize: '0.75rem',
    },
  }),
);

interface Props {
  outageDetail: GuestOutageDetail;
  lastUpdateOutageDataTime?: string;
  isCrewOnsite?: Maybe<boolean>;
}

const causeIconList = Object({
  'car accident': 'carAccident.svg',
  'traffic accident': 'carAccident.svg',

  maintenance: 'maintenance.svg',

  fire: 'fire.svg',
  'forest fire': 'fire.svg',
  'house fire': 'fire.svg',

  'heavy rain': 'heavyRain.svg',
  flooding: 'heavyRain.svg',

  lightning: 'lightening.svg',

  thunderstorm: 'thunderstorm.svg',
  weather: 'thunderstorm.svg',

  'high winds': 'highWind.svg',
  tornado: 'tornado.svg',

  ice: 'ice.svg',
  'heavy snow/ice': 'ice.svg',

  'heavy snow': 'snow.svg',
});

const getImageUrl = () => {
  const baseUrl = !(typeof window === 'undefined')
    ? window.location.origin
    : '';
  return `${baseUrl}/static/`;
};

const getCauseIcon = (cause: string | null | undefined) => {
  let causeIconName =
    causeIconList[cause ? cause.toLowerCase() : 'maintenance'];
  if (!causeIconName) {
    causeIconName = causeIconList['maintenance'];
  }
  return `${getImageUrl()}outageCauseIcons/${causeIconName}`;
};

const OutageDetailsSection: FunctionComponent<Props> = ({
  outageDetail,
  lastUpdateOutageDataTime,
  isCrewOnsite,
}) => {
  const classes = useStyles();
  const { t, richT } = useTranslation();

  const estimatedTimeOn = outageDetail?.estimatedTimeOn?.replace('am', 'AM');
  const estimatedTimeOut = outageDetail.estimatedTimeOut?.replace('am', 'AM');
  return (
    <div className={classes.root}>
      <Box className={classes.infoRow}>
        <ScheduleIcon
          className={classes.infoIcon}
          style={{ marginTop: '-15px' }}
        />
        <div className={classes.infoContent} style={{ marginTop: '-15px' }}>
          <Typography variant={'subtitle1'}>
            {isCrewOnsite
              ? t('ESTIMATED_RESTORATION')
              : t('PRELIMINARY_RESTORATION')}{' '}
            <Typography component="span" css={{ color: 'red' }}>
              *
            </Typography>
          </Typography>
          <Typography variant={'subtitle2'}>
            {formatEstimatedTimeOn(estimatedTimeOn)}
          </Typography>
          <Typography variant="subtitle1" className={classes.infoUpdate}>
            {lastUpdateOutageDataTime &&
              richT('OUTAGE_LAST_UPDATED', {
                OUTAGE_UPDATED_DATE: dayjs(
                  lastUpdateOutageDataTime,
                  'MMMM D, YYYY h:mm a',
                )
                  .format('MMM D, h:mm a')
                  .replace('May', 'May')
                  .replace('am', 'AM')
                  .replace('pm', 'PM'),
              })}
          </Typography>
        </div>
      </Box>
      <Divider />
      <Box className={classes.infoRow}>
        <img
          src={`${getImageUrl()}outageMapIcons/outageTimeout.svg`}
          className={classes.infoIcon}
        />
        <div className={classes.infoContent}>
          <Typography variant={'subtitle1'}>
            {t('OUTAGE_ESTIMATED_TIME_OUT')}
          </Typography>
          <Box flexGrow={1} />
          <Typography variant={'subtitle2'}>
            {formatEstimatedTimeOn(estimatedTimeOut)}
          </Typography>
        </div>
      </Box>
      <Divider />
      <Box className={classes.infoRow}>
        <img
          src={getCauseIcon(outageDetail.cause)}
          className={classes.infoIcon}
        />
        <div className={classes.infoContent}>
          <Typography variant={'subtitle1'}>{t('CAUSE')}</Typography>
          <Box flexGrow={1} />
          <Typography variant={'subtitle2'}>
            {outageDetail.cause ? outageDetail.cause : t('CAUSE_NOT_FOUND')}
          </Typography>
        </div>
      </Box>
      <Divider />
      <Box className={classes.infoRow}>
        <PeopleIcon className={classes.infoIcon} />
        <div className={classes.infoContent}>
          <Typography variant={'subtitle1'}>
            {t('OUTAGE_CUSTOMERS_AFFECTED')}
          </Typography>
          <Typography variant={'subtitle2'}>
            {outageDetail.numberOfCustomersAffected}
          </Typography>
        </div>
      </Box>
      <Divider />
    </div>
  );
};

export default OutageDetailsSection;
